import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useActiveSprint, useSprintEpics } from './useSprintData';
import { useActivityStream, type ActivityEvent } from '../api/activity';
import { telemetryApi, type TelemetryEvent, type TelemetryEventsResponse } from '../api/telemetry';
import { sprintApi, type IssueExecution, fetchSprintIssueExecutions, fetchSprintPlan, type SprintPlan } from '../api/sprint';

// Re-export for consumers that import from here
export type { IssueExecution };

/* ── Burndown data point ─────────────────────────────────── */

export interface BurndownPoint {
  timestamp: number;
  remaining: number;
  completed: number;
  issueId?: string;
  issueTitle?: string;
  status?: 'success' | 'failure';
}

/* ── Composite hook ──────────────────────────────────────── */

export function useSprintTelemetry(projectId: string | undefined, overrideSprintId?: string | null) {
  // Sprint resolution (building > failed > completed)
  const { data: autoSprint, isLoading: sprintLoading } = useActiveSprint(projectId);

  // If an override sprint ID is provided, fetch that sprint directly
  const { data: overrideSprint } = useQuery({
    queryKey: ['sprint', 'override', overrideSprintId],
    queryFn: async () => {
      // Fetch all sprints for the project and find the matching one
      const result = await sprintApi.list(projectId!);
      return result.sprints.find(s => s.id === overrideSprintId) || null;
    },
    enabled: !!projectId && !!overrideSprintId,
    staleTime: 30_000,
  });

  const sprint = (overrideSprintId && overrideSprint) ? overrideSprint : autoSprint;

  // Epics for the sprint
  const { data: epicsData } = useSprintEpics(sprint?.id);

  // Issue executions at 5s refresh (only poll for active builds)
  const { data: issueExecutions } = useQuery({
    queryKey: ['sprint-issue-executions', sprint?.batchId],
    queryFn: () => fetchSprintIssueExecutions(sprint!.batchId!),
    enabled: !!sprint?.batchId,
    refetchInterval: sprint?.status === 'building' ? 5_000 : false,
    staleTime: 3_000,
  });

  // Active sprites — scoped to this sprint's batchId
  const { data: spritesData } = useQuery({
    queryKey: ['coder', 'sprites', sprint?.batchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (sprint?.batchId) params.set('batchId', sprint.batchId);
      const res = await fetch(`/api/orchestration/signals/sprites?${params}`);
      if (!res.ok) throw new Error('Failed to fetch sprites');
      return res.json() as Promise<{
        sprites: Array<{
          spriteId: string;
          agentId: string;
          status: string;
          lastHeartbeat: number;
          batchId?: string;
          metrics?: any;
        }>;
        count: number;
      }>;
    },
    enabled: !!sprint?.batchId,
    refetchInterval: sprint?.status === 'building' ? 15_000 : false,
    staleTime: 10_000,
  });

  // SSE stream
  const stream = useActivityStream({ enabled: !!sprint?.batchId });

  // Filter stream events to this sprint's batchId (prefix match for child workflows)
  const sprintEvents = useMemo(() => {
    if (!sprint?.batchId || !stream.events.length) return [];
    return stream.events.filter(
      (e: ActivityEvent) =>
        e.workflowId === sprint.batchId ||
        e.workflowId?.startsWith(sprint.batchId + '-') ||
        e.metadata?.workflowId === sprint.batchId ||
        (typeof e.metadata?.workflowId === 'string' && e.metadata.workflowId.startsWith(sprint.batchId + '-')),
    );
  }, [stream.events, sprint?.batchId]);

  // Structured telemetry events from /telemetry/events
  // Query by batchId (not workflowId) to include child workflow events
  const { data: telemetryData } = useQuery({
    queryKey: ['telemetry', 'events', { batchId: sprint?.batchId, limit: 500 }],
    queryFn: () => telemetryApi.getEvents({ batchId: sprint!.batchId!, limit: 500, excludeEventTypes: 'sprite_heartbeat' }),
    enabled: !!sprint?.batchId,
    refetchInterval: sprint?.status === 'building' ? 5_000 : false,
    staleTime: 3_000,
  });

  // Sprint plan data (epic->story tree with execution overlay)
  // Must be declared before burndownData which references sprintPlan
  const { data: sprintPlan } = useQuery({
    queryKey: ['sprint-plan', sprint?.id],
    queryFn: () => fetchSprintPlan(sprint!.id!),
    enabled: !!sprint?.id,
    refetchInterval: sprint?.status === 'building' ? 10_000 : false,
    staleTime: 5_000,
  });

  // Derive burndown data from issue executions
  const burndownData = useMemo<BurndownPoint[]>(() => {
    const execs = issueExecutions || [];
    const total = Math.max(sprintPlan?.totalStories || 0, execs.length);
    if (total === 0) return [];

    // Start point
    const startTime = sprint?.startedAt ? new Date(sprint.startedAt).getTime() : Date.now();
    const points: BurndownPoint[] = [{ timestamp: startTime, remaining: total, completed: 0 }];

    // Sort resolved executions (success + failure) by completedAt
    const resolved = execs
      .filter((e) => e.completedAt && (e.status === 'success' || e.status === 'failure'))
      .sort((a, b) => new Date(a.completedAt!).getTime() - new Date(b.completedAt!).getTime());

    let remaining = total;
    let completed = 0;
    for (const exec of resolved) {
      remaining--;
      if (exec.status === 'success') completed++;
      points.push({
        timestamp: new Date(exec.completedAt!).getTime(),
        remaining,
        completed,
        issueId: exec.issueId,
        issueTitle: exec.issueTitle ?? undefined,
        status: exec.status as 'success' | 'failure',
      });
    }

    // Current point
    if (remaining > 0) {
      points.push({ timestamp: Date.now(), remaining, completed });
    }

    return points;
  }, [issueExecutions, sprint?.startedAt, sprintPlan]);

  const telemetryEvents: TelemetryEvent[] = telemetryData?.events || [];

  // Derive sprint lifecycle phases from structured events
  const sprintPhases = useMemo(() => {
    if (!telemetryEvents.length) return [];
    const lifecycleTypes = new Set([
      'workflow_started', 'wave_plan_computed', 'sprite_spawned',
      'story_execution_started', 'story_execution_completed',
      'verification_completed', 'sprite_destroyed',
      'test_wave_started', 'test_issue_execution_started',
      'commit_made', 'batch_execution_completed',
      'agent_started', 'agent_completed',
      'claude_session_result',
      // Full-cycle phase events
      'build_started', 'build_completed',
      'test_started', 'repair_started', 'repair_completed',
    ]);
    return telemetryEvents
      .filter((e) => lifecycleTypes.has(e.eventType))
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [telemetryEvents]);

  // Quality gate summary derived from telemetry events
  const qualityGates = useMemo(() => {
    if (!telemetryEvents.length) return { gates: [] as string[], byStory: {} as Record<string, Record<string, 'pass' | 'fail' | 'skipped'>> };

    const gateTypes = new Set([
      'content_validation_completed', 'story_tsc_check_completed',
      'ac_validation_completed', 'code_review_completed',
      'sprite_lint_passed', 'sprite_lint_failed',
      'sprite_typecheck_passed', 'sprite_typecheck_failed',
    ]);

    const gateEvents = telemetryEvents.filter(e => gateTypes.has(e.eventType));

    const gateNameMap: Record<string, string> = {
      content_validation_completed: 'Content',
      story_tsc_check_completed: 'TypeCheck',
      ac_validation_completed: 'Acceptance',
      code_review_completed: 'Review',
      sprite_lint_passed: 'Lint',
      sprite_lint_failed: 'Lint',
      sprite_typecheck_passed: 'TypeCheck',
      sprite_typecheck_failed: 'TypeCheck',
    };

    const byStory: Record<string, Record<string, 'pass' | 'fail' | 'skipped'>> = {};

    for (const e of gateEvents) {
      const storyId = (e.payload?.storyId as string) || e.issueId || 'unknown';
      const gateName = gateNameMap[e.eventType] || e.eventType;
      const passed = e.eventType.includes('passed') || e.eventType.includes('completed')
        ? (e.severity !== 'error' && e.severity !== 'critical') : false;

      if (!byStory[storyId]) byStory[storyId] = {};
      byStory[storyId][gateName] = passed ? 'pass' : 'fail';
    }

    const allGateNames = [...new Set(gateEvents.map(e => gateNameMap[e.eventType] || e.eventType))];

    return { gates: allGateNames, byStory };
  }, [telemetryEvents]);

  // Token usage from claude_session_result events
  const tokenUsage = useMemo(() => {
    const sessionEvents = telemetryEvents.filter(e => e.eventType === 'claude_session_result');

    const byStory: Record<string, { input: number; output: number; cacheRead: number; total: number }> = {};
    let totalInput = 0, totalOutput = 0, totalCacheRead = 0;

    for (const e of sessionEvents) {
      const p = e.payload || {};
      const input = Number(p.inputTokens) || 0;
      const output = Number(p.outputTokens) || 0;
      const cacheRead = Number(p.cacheReadTokens) || 0;

      totalInput += input;
      totalOutput += output;
      totalCacheRead += cacheRead;

      const storyId = (p.storyId as string) || (p.taskId as string) || e.issueId || 'unknown';
      if (!byStory[storyId]) byStory[storyId] = { input: 0, output: 0, cacheRead: 0, total: 0 };
      byStory[storyId].input += input;
      byStory[storyId].output += output;
      byStory[storyId].cacheRead += cacheRead;
      byStory[storyId].total += input + output;
    }

    return {
      totalInput,
      totalOutput,
      totalCacheRead,
      total: totalInput + totalOutput,
      byStory,
      sessionCount: sessionEvents.length,
    };
  }, [telemetryEvents]);

  const epics = epicsData?.epics || [];
  const sprites = spritesData?.sprites || [];

  return {
    sprint: sprint ?? null,
    epics,
    issueExecutions: issueExecutions || [],
    sprites,
    stream,
    sprintEvents,
    burndownData,
    telemetryEvents,
    sprintPhases,
    sprintPlan: sprintPlan ?? null,
    qualityGates,
    tokenUsage,
    isLoading: sprintLoading,
  };
}
